const article = {
  slug: "nar-commission-settlement-listing-agents",
  cluster: "agent",
  title: "The NAR Commission Settlement and Listing Agents: What Changed",
  description:
    "What the NAR commission settlement changed for listing agents: buyer-broker compensation, written buyer agreements, and how commissions are now discussed. A careful, general explainer.",
  audience: "Real estate agents",
  funnelStage: "TOFU",
  datePublished: "2026-06-25",
  dateModified: "2026-06-25",
  readingTime: "7 min read",
  tldr: "The NAR settlement changed how buyer-broker compensation is handled: offers of buyer-agent pay can no longer be advertised on the MLS, and buyers typically sign written agreements before touring. For listing agents, that means more upfront commission conversations with sellers and clearer, decoupled fee discussions. Rules keep evolving, so always verify current requirements with your broker.",
  blocks: [
    {
      type: "p",
      spans: [
        "The National Association of Realtors (NAR) settlement reshaped how real estate commissions are discussed and advertised in the United States. For listing agents, the headline changes are simple to state: offers of compensation to a buyer's broker can no longer be posted on the MLS, and buyers generally sign a written agreement with their agent before touring homes. The practical effect is that commission is now a more explicit, upfront conversation with every seller you represent.",
      ],
    },
    {
      type: "callout",
      spans: [
        "This article is a general explainer, not legal advice. Settlement terms, state laws, and MLS and brokerage policies continue to evolve. Always confirm the current requirements with your broker and your local MLS before you rely on any specific rule.",
      ],
    },
    {
      type: "h2",
      text: "What did the NAR settlement actually change?",
      id: "what-changed",
    },
    {
      type: "p",
      spans: [
        "At a high level, the settlement resolved litigation over how buyer-agent commissions were communicated and shared. Two structural changes matter most to listing agents working day to day:",
      ],
    },
    {
      type: "ul",
      items: [
        [
          "Offers of buyer-broker compensation are no longer displayed on the MLS. A seller may still choose to offer compensation to a buyer's agent, but that offer can't be advertised through the multiple listing service the way it commonly was before.",
        ],
        [
          "Buyers typically sign a written representation agreement before their agent tours homes with them. That agreement spells out how the buyer's agent is paid, which shifts some compensation conversations earlier and onto the buyer side.",
        ],
      ],
    },
    {
      type: "p",
      spans: [
        "What did not change: commissions were already negotiable and set by agreement, not by any fixed rate. The settlement reinforced that there is no standard or required commission, and that fees should be discussed openly rather than assumed.",
      ],
    },
    {
      type: "h2",
      text: "How does this change the listing-side commission conversation?",
      id: "listing-side-conversation",
    },
    {
      type: "p",
      spans: [
        "Because the MLS no longer carries a blanket offer of buyer-agent compensation, you and your seller now decide deliberately how, or whether, to address buyer-side pay. That makes the listing appointment the moment where the full compensation picture gets set. Walk the seller through the realistic options rather than presenting a single number as fixed.",
      ],
    },
    {
      type: "p",
      spans: [
        "In practice, sellers tend to ask one of a few questions: do I have to pay the buyer's agent, what happens to my pool of interested buyers if I don't, and how is your fee separate from theirs. Be ready to explain that your listing fee and any concession toward a buyer's agent are two distinct decisions. Treating them as one bundled number is exactly the habit the settlement pushed the industry away from.",
      ],
    },
    {
      type: "table",
      headers: [
        "Topic",
        "Before the settlement (common practice)",
        "After the settlement (general direction)",
      ],
      rows: [
        [
          "Buyer-agent offer on MLS",
          "Compensation offer commonly published on the MLS",
          "No longer advertised on the MLS; handled off-MLS or via negotiation",
        ],
        [
          "When buyer pay is set",
          "Often assumed from the published MLS offer",
          "Discussed explicitly, often once an offer or buyer agreement is in play",
        ],
        [
          "Listing-fee discussion",
          "Sometimes presented as a single combined rate",
          "Listing fee and any buyer-side concession discussed as separate decisions",
        ],
        [
          "Buyer representation",
          "Written agreement not always required before touring",
          "Written buyer agreement typically signed before touring",
        ],
      ],
    },
    {
      type: "h2",
      text: "Can a seller still offer to pay the buyer's agent?",
      id: "can-sellers-still-offer",
    },
    {
      type: "p",
      spans: [
        "Generally, yes. A seller can still choose to offer a concession toward the buyer's agent commission; that offer simply isn't advertised through the MLS. It can be communicated in other permitted ways and is commonly negotiated as part of the purchase agreement. Whether a particular form of offer or communication is allowed depends on your MLS rules and state regulations, so verify the mechanics locally.",
      ],
    },
    {
      type: "p",
      spans: [
        "The strategic question for your seller is no longer just how much, but whether offering a buyer-side concession widens the pool of buyers who can comfortably afford the home. A buyer who owes their own agent a fee out of pocket has less cash for the purchase. Framing a concession as a tool to keep the home accessible to more buyers is often more useful to a seller than framing it as an obligation.",
      ],
    },
    {
      type: "callout",
      spans: [
        "Reframe for sellers: a buyer-agent concession isn't a rule you owe, it's a lever that can keep your home affordable to a larger pool of buyers. Present it as a strategy decision, not a fixed cost.",
      ],
    },
    {
      type: "h2",
      text: "How should listing agents prepare for the appointment now?",
      id: "prepare-for-the-appointment",
    },
    {
      type: "p",
      spans: [
        "Win the conversation by being the agent who explains the new landscape clearly instead of dodging it. Sellers have read the headlines and many are confused; clarity is a competitive advantage. Build your commission talk into your standard listing presentation so it never feels like an afterthought. A structured ",
        {
          text: "listing appointment checklist",
          href: "/resources/listing-appointment-checklist",
        },
        " helps you cover compensation, marketing, and pricing in a logical order, and a strong ",
        {
          text: "pre-listing package",
          href: "/resources/pre-listing-package-template",
        },
        " lets the seller absorb the details before you even sit down.",
      ],
    },
    {
      type: "ol",
      items: [
        [
          "Explain that commissions are negotiable and that there is no standard rate, in plain language.",
        ],
        [
          "Separate your listing fee from any concession toward a buyer's agent so the seller sees two distinct decisions.",
        ],
        [
          "Walk through how a buyer-side concession can affect the size of the buyer pool, using a clearly labeled illustrative example rather than a promised figure.",
        ],
        [
          "Put the agreed terms in writing and confirm anything MLS- or state-specific with your broker.",
        ],
        [
          "Document the value you deliver — pricing strategy, marketing reach, and negotiation — so the fee conversation rests on value, not on a number pulled from the MLS.",
        ],
      ],
    },
    {
      type: "p",
      spans: [
        "Because fees now compete on demonstrated value, it pays to show up with evidence. A sharp pricing case backed by a real ",
        {
          text: "comparative market analysis",
          href: "/resources/what-is-a-cma",
        },
        " and a concrete ",
        {
          text: "listing marketing plan",
          href: "/resources/listing-marketing-plan",
        },
        " make your fee easy to justify. If you want a repeatable way to walk in prepared, our broader guide on ",
        {
          text: "how to win the listing appointment",
          href: "/resources/how-to-win-the-listing-appointment",
        },
        " ties the pricing, marketing, and commission conversations together.",
      ],
    },
    {
      type: "h2",
      text: "What stays the same for listing agents?",
      id: "what-stays-the-same",
    },
    {
      type: "p",
      spans: [
        "Most of your core job is unchanged. You still price the home accurately, market it well, manage showings and feedback, and negotiate the best terms for your seller. The settlement changed how compensation is communicated and how buyer representation is documented; it did not change the fundamentals of getting a home sold for the most money in the least time. Agents who were already transparent about fees and who led with value have the least to adjust.",
      ],
    },
    {
      type: "stat",
      value: "Commissions have always been negotiable",
      label: "The settlement reinforced this long-standing principle rather than creating it",
    },
    {
      type: "h2",
      text: "Why does this matter for how you win listings?",
      id: "why-it-matters",
    },
    {
      type: "p",
      spans: [
        "In a market where sellers are more fee-aware than ever, the agents who win are the ones who can clearly articulate what they do and why it's worth it. That means a confident, transparent commission conversation, a tight pricing argument, and marketing the seller can actually see. Preparation is the differentiator — and that's exactly where a same-day listing prep or a polished pre-listing presentation earns its keep. When you can ",
        {
          text: "get started",
          href: "/get-started",
        },
        " with a data-grounded plan in hand, the commission question becomes one part of a larger, credible case rather than the whole conversation.",
      ],
    },
  ],
  faqs: [
    {
      question: "Does the NAR settlement mean sellers no longer pay buyer-agent commissions?",
      answer: [
        {
          type: "p",
          spans: [
            "No. Sellers can still choose to offer a concession toward the buyer's agent's commission. What changed is that this offer can no longer be advertised on the MLS, so it's now negotiated more explicitly, often as part of the purchase agreement. Whether a seller offers anything is a strategy decision, not a requirement, and the exact mechanics depend on your MLS and state rules — confirm them with your broker.",
          ],
        },
      ],
    },
    {
      question: "Do listing agents have to change their commission?",
      answer: [
        {
          type: "p",
          spans: [
            "The settlement did not set, cap, or change any commission. Commissions have always been negotiable and individually agreed upon. The practical shift is that compensation is now discussed more openly and earlier, and listing agents typically separate their own fee from any concession toward the buyer's agent rather than presenting a single combined number.",
          ],
        },
      ],
    },
    {
      question: "Can a buyer-agent compensation offer still appear anywhere?",
      answer: [
        {
          type: "p",
          spans: [
            "It can no longer be displayed on the MLS, but a seller may still communicate an offer of compensation through other permitted channels and negotiate it within the transaction. The allowed methods vary by MLS and by state, so verify what's currently permitted in your market before relying on any specific approach.",
          ],
        },
      ],
    },
    {
      question: "What's the single most important thing for a listing agent to do now?",
      answer: [
        {
          type: "p",
          spans: [
            "Build a clear, transparent commission conversation into your standard listing presentation. Explain that commissions are negotiable, separate your fee from any buyer-side concession, and tie the discussion to the value you deliver through pricing, marketing, and negotiation. A prepared, plain-English explanation is now a real competitive advantage with fee-aware sellers.",
          ],
        },
      ],
    },
    {
      question: "Is this article legal advice?",
      answer: [
        {
          type: "p",
          spans: [
            "No. This is a general explainer for context only. Settlement terms, court rulings, state laws, and MLS and brokerage policies continue to evolve. Always verify the current requirements with your broker, your local MLS, and qualified legal counsel before acting on any specific rule.",
          ],
        },
      ],
    },
  ],
  related: [
    "listing-appointment-checklist",
    "pre-listing-package-template",
    "what-is-a-cma",
    "listing-marketing-plan",
  ],
};

export default article;
